import { cloneElement, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Dimensions } from "react-native-web";
import { isWeb, useComposedRefs, View } from "@tamagui/core";
import { useObservabilitySafe } from "./useObservabilitySafe";
import { jsx } from "react/jsx-runtime";
const useTrackComponentInViewForWeb = (eventName, analyticsName, options) => {
  const { trackJourneyEvent } = useObservabilitySafe(), [inViewRef, inView] = useInView({
    // https://react-intersection-observer.vercel.app/?path=/docs/useinview-hook--basic
    // threshold: Number between 0 and 1 indicates the percentage that should be visible
    // before triggering. (defaults to 100% unless an override is provided)
    threshold: 1,
    skip: !analyticsName,
    triggerOnce: !0,
    ...options
  });
  return useEffect(() => {
    inView && trackJourneyEvent("platform", eventName, { targetName: analyticsName });
  }, [inView, analyticsName]), useMemo(() => inViewRef, [inViewRef]);
}, WebComponentWithViewTracking = forwardRef(function(props, ref) {
  const { Component, analyticsName, eventName, inViewThreshold } = props, inViewRef = useTrackComponentInViewForWeb(eventName, analyticsName, {
    threshold: inViewThreshold
  });
  return cloneElement(Component, {
    ref: useComposedRefs(inViewRef, ref)
  });
}), MobileComponentWithViewTracking = forwardRef(function(props, _ref) {
  const { Component, analyticsName, eventName } = props;
  let inViewThreshold = props.inViewThreshold || 1;
  const { trackJourneyEvent } = useObservabilitySafe(), inViewRef = useRef(null), [trackedInView, setTrackedInView] = useState(!1), interval = useRef(null), startWatching = useCallback(() => {
    interval.current || trackedInView || (interval.current = setInterval(() => {
      !inViewRef || !inViewRef.current || inViewRef.current?.measure(
        async (_x, _y, width, height, pageX, pageY) => {
          const window = Dimensions.get("window");
          (height > window.height || width > window.width) && (inViewThreshold = // eslint-disable-next-line react-hooks/exhaustive-deps
          (height > window.height ? window.height / height : window.width / width) - 0.01);
          const componentHeight = height * inViewThreshold, componentWidth = width * inViewThreshold, rectBottom = pageY + componentHeight, rectTop = pageY, rectRight = pageX + componentWidth, isVisible = rectBottom !== 0 && // Check if the bottom edge is not at the top (initialized value)
          rectTop >= 0 && // Check if the top edge is greater than or equal to the top of the viewport
          rectBottom <= window.height && // Check if the bottom edge is within the inViewThreshold percentage of the viewport height
          rectRight >= 0 && // Check if the width is greater than 0 (initialized value)
          rectRight <= window.width;
          trackedInView !== isVisible && (setTrackedInView(isVisible), trackJourneyEvent("platform", eventName, { targetName: analyticsName }));
        }
      );
    }, 300));
  }, [trackedInView, inViewThreshold, trackJourneyEvent, eventName, analyticsName]), stopWatching = useCallback(() => {
    interval.current && (clearInterval(interval.current), interval.current = null);
  }, []);
  return useEffect(() => (trackedInView || startWatching(), stopWatching), [startWatching, stopWatching, trackedInView]), /* @__PURE__ */ jsx(
    View,
    {
      ref: inViewRef,
      collapsable: !1,
      children: Component
    }
  );
}), ComponentWithViewTracking = forwardRef(function(props, ref) {
  const { Component, analyticsName, eventName, inViewThreshold } = props;
  return isWeb ? /* @__PURE__ */ jsx(
    WebComponentWithViewTracking,
    {
      Component,
      analyticsName,
      eventName,
      inViewThreshold,
      ref
    }
  ) : /* @__PURE__ */ jsx(
    MobileComponentWithViewTracking,
    {
      Component,
      analyticsName,
      eventName,
      inViewThreshold,
      ref
    }
  );
});
export {
  ComponentWithViewTracking
};
